/**
 * pipelineApi · shared axios client for Overwatch Auto-System pipeline endpoints
 * ─────────────────────────────────────────────────────────────────────────────
 * Wraps the four read/write endpoints introduced in Phase 2/3 so components
 * don't each re-declare their own client.
 *
 *   GET  /appraisals/{id}/pipeline
 *   GET  /workflow-audit?…
 *   POST /workflow-runs             (start a run — rejects if status is locked)
 *   PATCH /workflow-runs/{run_id}
 *
 * Also exports the canonical status ORDER / LABELS mirroring the backend so UI
 * doesn't have to re-fetch the constants.
 */
import axios from 'axios';
import { useMemo } from 'react';
import { useStages } from '@/lib/stages';

export const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export const STATUS_ORDER = [
  'client_data', 'appraisal_booked', 'quote_sent',
  'accepted', 'declined', 'work_booked',
  'work_in_progress', 'completed', 'invoiced', 'overdue', 'paid',
];

export const STATUS_LABELS = {
  client_data:      'Client Data',
  appraisal_booked: 'Appraisal Booked',
  quote_sent:       'Quoting',
  accepted:         'Accepted',
  declined:         'Declined',
  work_booked:      'Work Booked',
  work_in_progress: 'Work In Progress',
  completed:        'Completed',
  invoiced:         'Invoiced',
  overdue:          'Overdue',
  paid:             'Paid',
};

export const STATE_ICONS = {
  complete:    '✓',
  in_progress: '▶',
  unstarted:   '○',
  locked:      '🔒',
  skipped:     '—',
};

export const STATE_COLOURS = {
  complete:    'emerald',
  in_progress: 'sky',
  unstarted:   'slate',
  locked:      'slate',
  skipped:     'stone',
};

export const getWorkflow = (statusKey) =>
  api.get(`/status-workflows/workflow/${statusKey}`).then(r => r.data);

export const getPipeline = (appraisalId) =>
  api.get(`/appraisals/${appraisalId}/pipeline`).then(r => r.data);

export const startWorkflowRun = ({ statusKey, appraisalId, entryChoice }) =>
  api.post('/workflow-runs', {
    status_key: statusKey,
    appraisal_id: appraisalId,
    entry_choice: entryChoice,
  }).then(r => r.data);

export const listRuns = (params = {}) =>
  api.get('/workflow-runs', { params }).then(r => r.data);

export const queryAudit = (params) =>
  api.get('/workflow-audit', { params }).then(r => r.data);

// Designer labels win (single stage list) — renames in the designer show up everywhere.
export function useStatusLabels() {
  const stages = useStages(api);
  return useMemo(() => ({ ...STATUS_LABELS, ...Object.fromEntries(stages.map((st) => [st.key, st.label])) }), [stages]);
}
